import { formatNumberColumnValue, LocaleCode } from '@doss/common-all'
import { FormatPreset } from '@doss/prisma/types'
import { AggregationOperation, DataType } from '~/common/generated/apollo-operations'
import { TranslationKeys } from '~/common/generated/i18n.generated'

/** Core numeric metric types shared across aggregation contexts */
export type CoreMetricType = 'count' | 'sum' | 'avg' | 'min' | 'max'

/** Metric type for column aggregation row (includes 'none' option) */
export type MetricType = 'none' | CoreMetricType

/**
 * Maps MetricType to backend AggregationOperation enum
 */
export const METRIC_TO_AGGREGATION_OPERATION: Record<
  Exclude<MetricType, 'none'>,
  AggregationOperation
> = {
  count: AggregationOperation.COUNT,
  sum: AggregationOperation.SUM,
  avg: AggregationOperation.AVG,
  min: AggregationOperation.MIN,
  max: AggregationOperation.MAX,
}

/**
 * Check if a column type supports numeric aggregations (sum, avg, min, max).
 * Note: This intentionally excludes ID columns (unlike isNumericColumn in common-all)
 * because ID columns should not be aggregated.
 */
export const isNumericColumnType = (type: DataType): boolean => {
  return type === DataType.NUMBER || type === DataType.FORMULA_NUMBER || type === DataType.FORMULA
}

/**
 * Get metric options based on column type
 */
export const getMetricOptions = (
  columnType: DataType,
  t: (key: TranslationKeys) => string,
): { value: MetricType; label: string }[] => {
  if (isNumericColumnType(columnType)) {
    return [
      { value: 'none', label: '-' },
      { value: 'sum', label: t('table.sum') },
      { value: 'count', label: t('table.count') },
      { value: 'avg', label: t('table.average') },
      { value: 'min', label: t('table.min') },
      { value: 'max', label: t('table.max') },
    ]
  }
  // Non-numeric columns only support count
  return [
    { value: 'none', label: '-' },
    { value: 'count', label: t('table.count') },
  ]
}

/**
 * Get abbreviated label for a metric type (e.g., "SUM", "AVG")
 */
export const getAbbreviatedMetricLabel = (metric: MetricType): string => {
  switch (metric) {
    case 'sum':
      return 'SUM'
    case 'count':
      return 'COUNT'
    case 'avg':
      return 'AVG'
    case 'min':
      return 'MIN'
    case 'max':
      return 'MAX'
    default:
      return ''
  }
}

/**
 * Format a numeric value with basic locale formatting based on metric type.
 * This is the shared formatting logic used by both cell selection metrics and column aggregations.
 * - count: plain integer
 * - sum/min/max: locale-formatted number
 * - avg: locale-formatted with max 2 decimal places
 */
export const formatBasicNumericMetric = (
  value: number | null | undefined,
  metricType: CoreMetricType,
  localeCode: LocaleCode,
): string => {
  if (value === null || value === undefined) {
    return '-'
  }

  switch (metricType) {
    case 'count':
      return `${value}`
    case 'sum':
    case 'min':
    case 'max':
      return value.toLocaleString(localeCode)
    case 'avg':
      return value.toLocaleString(localeCode, { maximumFractionDigits: 2 })
    default:
      return value.toString()
  }
}

/**
 * Format the aggregation value based on metric type and column format settings.
 * When formatPreset is provided, uses the column's number formatting (currency, percent, etc.).
 * For 'count', always uses plain number formatting regardless of column settings.
 * For 'avg', applies column formatting but ensures reasonable decimal places.
 */
export const formatMetricValue = (
  value: number | null | undefined,
  metricType: MetricType,
  localeCode: LocaleCode,
  formatPreset?: FormatPreset | null,
  formatOptions?: unknown,
): string => {
  if (value === null || value === undefined) {
    return '-'
  }

  // Count is always displayed as a plain integer regardless of column formatting
  if (metricType === 'count') {
    return `${value}`
  }

  // If the column has a format preset and valid format options, use the column's formatting
  // For currency, formatOptions must include a valid currency code
  if (formatPreset && formatOptions) {
    try {
      const formatted = formatNumberColumnValue(value, { formatPreset, formatOptions }, localeCode)
      // If formatting returned an empty result or just symbols, fall back to basic formatting
      // This handles edge cases like malformed formatOptions
      if (formatted && /\d/.test(formatted)) {
        return formatted
      }
    } catch {
      // If formatting fails, fall back to basic locale formatting
    }
  }

  // Fallback to basic locale formatting when no format preset or formatting failed
  if (metricType === 'none') {
    return ''
  }
  return formatBasicNumericMetric(value, metricType, localeCode)
}
